export type LeadStatus = 'new' | 'contacted' | 'interested' | 'converted' | 'lost';

export type LeadSource =
    | 'field_visit'
    | 'whatsapp'
    | 'referral'
    | 'call'
    | 'walk_in'
    | 'google_places'
    | 'import';

export type LeadType =
    | 'restaurant'
    | 'hotel'
    | 'bakery'
    | 'caterer'
    | 'retail_shop'
    | 'supermarket'
    | 'cloud_kitchen'
    | 'canteen'
    | 'other';

export type LeadActivityType =
    | 'note'
    | 'call'
    | 'visit'
    | 'whatsapp'
    | 'status_change'
    | 'follow_up'
    | 'conversion'
    | 'photo';

/** Follow-up bucket, matched to the backend's `follow_up` query param. */
export type FollowUpBucket = 'today' | 'overdue' | 'week' | 'none';

export interface LeadPartySimple {
    id: number;
    name: string;
    phone?: string | null;
}

export interface Lead {
    id: number;
    organization_id: number;
    business_name: string;
    contact_person?: string | null;
    phone?: string | null;
    alt_phone?: string | null;
    email?: string | null;
    lead_type?: LeadType | null;
    source: LeadSource;
    source_ref?: string | null;
    address?: string | null;
    area?: string | null;
    city?: string | null;
    pincode?: string | null;
    /** "lat,lng" — same format as party.geo_location, so it copies over on convert. */
    geo_location?: string | null;
    status: LeadStatus;
    lost_reason?: string | null;
    next_follow_up_date?: string | null;
    last_activity_at?: string | null;
    estimated_monthly_value?: string | number | null;
    notes?: string | null;
    site_id?: number | null;
    assigned_to_party_id?: number | null;
    converted_party_id?: number | null;
    converted_at?: string | null;
    created_at?: string | null;
    updated_at?: string | null;
    assigned_to?: LeadPartySimple | null;
    converted_party?: LeadPartySimple | null;
    site?: { id: number; name: string } | null;
}

export interface LeadListItem {
    id: number;
    business_name: string;
    contact_person?: string | null;
    phone?: string | null;
    lead_type?: LeadType | null;
    source: LeadSource;
    area?: string | null;
    status: LeadStatus;
    geo_location?: string | null;
    next_follow_up_date?: string | null;
    last_activity_at?: string | null;
    estimated_monthly_value?: string | number | null;
    assigned_to_party_id?: number | null;
    assigned_to_name?: string | null;
    converted_party_id?: number | null;
    site_id?: number | null;
    created_at?: string | null;
    is_overdue: boolean;
}

export interface LeadActivity {
    id: number;
    lead_id: number;
    activity_type: LeadActivityType;
    notes?: string | null;
    old_status?: LeadStatus | null;
    new_status?: LeadStatus | null;
    next_follow_up_date?: string | null;
    geo_location?: string | null;
    created_at?: string | null;
    created_by?: number | null;
    created_by_name?: string | null;
}

export interface LeadDetail extends Lead {
    activities: LeadActivity[];
    attachment_count: number;
}

export interface DuplicatePartyMatch {
    id: number;
    name: string;
    phone?: string | null;
    party_group?: string | null;
    responsible_party_name?: string | null;
}

export interface DuplicateLeadMatch {
    id: number;
    business_name: string;
    phone?: string | null;
    status: LeadStatus;
    assigned_to_name?: string | null;
}

export interface DuplicateWarning {
    parties: DuplicatePartyMatch[];
    leads: DuplicateLeadMatch[];
}

export interface LeadCreateResponse {
    lead: Lead;
    duplicate_warning?: DuplicateWarning | null;
}

export interface LeadListResponse {
    items: LeadListItem[];
    total: number;
}

export interface LeadSummary {
    total: number;
    open_total: number;
    by_status: Record<LeadStatus, number>;
    due_today: number;
    overdue: number;
    due_this_week: number;
    no_follow_up: number;
    converted_this_month: number;
}

export interface LeadCreatePayload {
    business_name: string;
    contact_person?: string;
    phone?: string;
    alt_phone?: string;
    email?: string;
    lead_type?: LeadType;
    source?: LeadSource;
    address?: string;
    area?: string;
    city?: string;
    pincode?: string;
    geo_location?: string;
    estimated_monthly_value?: string;
    notes?: string;
    site_id?: number | null;
    next_follow_up_date?: string;
}

export interface LeadActivityPayload {
    activity_type: LeadActivityType;
    notes?: string;
    status?: LeadStatus;
    lost_reason?: string;
    next_follow_up_date?: string;
    /** Explicit, because `next_follow_up_date: null` can't distinguish "unset" from "clear". */
    clear_follow_up?: boolean;
    geo_location?: string;
}

export interface LeadConvertPayload {
    party_group_id: number;
    site_id: number;
    primary_party_type?: string;
    party_name?: string;
    gstin?: string;
    link_to_existing_party_id?: number;
    notes?: string;
}

export interface LeadConvertResponse {
    lead: Lead;
    party: LeadPartySimple;
    created_new_party: boolean;
}

export const LEAD_STATUS_LABELS: Record<LeadStatus, string> = {
    new: 'New',
    contacted: 'Contacted',
    interested: 'Interested',
    converted: 'Converted',
    lost: 'Lost',
};

export const LEAD_TYPE_LABELS: Record<LeadType, string> = {
    restaurant: 'Restaurant',
    hotel: 'Hotel',
    bakery: 'Bakery',
    caterer: 'Caterer',
    retail_shop: 'Shop',
    supermarket: 'Supermarket',
    cloud_kitchen: 'Cloud Kitchen',
    canteen: 'Canteen',
    other: 'Other',
};

export const LEAD_SOURCE_LABELS: Record<LeadSource, string> = {
    field_visit: 'Field visit',
    whatsapp: 'WhatsApp',
    referral: 'Referral',
    call: 'Call',
    walk_in: 'Walk-in',
    google_places: 'Google Places',
    import: 'Import',
};
